/**
 * Toast Notification Component
 * Provides user feedback with animated notifications
 */

import React, { useState } from "react";
import { useToastStore } from "./toastStore";

// Re-export toast utility for backward compatibility
export { toast } from "./toastUtils";

// Individual Toast Component
function ToastItem({
  toast,
}: {
  toast: { id: string; message: string; type: string; duration?: number };
}) {
  const { removeToast } = useToastStore();
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => removeToast(toast.id), 300);
  };

  const getToastStyles = () => {
    const baseStyles =
      "flex items-center justify-between p-4 rounded-lg shadow-lg max-w-sm w-full transform transition-all duration-300 ease-in-out";

    switch (toast.type) {
      case "success":
        return `${baseStyles} bg-green-500 text-white`;
      case "error":
        return `${baseStyles} bg-red-500 text-white`;
      case "warning":
        return `${baseStyles} bg-yellow-500 text-black`;
      case "info":
        return `${baseStyles} bg-blue-500 text-white`;
      default:
        return `${baseStyles} bg-gray-500 text-white`;
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "warning":
        return "⚠";
      case "info":
        return "ℹ";
      default:
        return "•";
    }
  };

  return (
    <div
      className={`${getToastStyles()} ${
        isExiting ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        <span className="text-lg font-bold">{getIcon()}</span>
        <span className="text-sm font-medium">{toast.message}</span>
      </div>
      <button
        onClick={handleClose}
        className="ml-4 text-white hover:text-gray-200 transition-colors"
        aria-label="Close notification"
      >
        <span className="text-lg">×</span>
      </button>
    </div>
  );
}

// Toast Container Component
export function ToastContainer() {
  const { toasts } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
